/**
 * Microsoft Integration API
 */
import api from './index'
import type {
  OutlookEmail,
  OutlookFolder,
  CalendarEvent,
  OneDriveFile,
  OneDriveFolder,
  MicrosoftAccount,
} from '@/types'

// ============================================================================
// Types
// ============================================================================

export interface MicrosoftConfig {
  configured: boolean
  has_client_id: boolean
  has_client_secret: boolean
  has_tenant_id: boolean
  has_redirect_uri: boolean
  message: string
}

// ============================================================================
// Connection
// ============================================================================

export const microsoftApi = {
  /**
   * Check if Microsoft integration is configured
   */
  async checkConfig(): Promise<MicrosoftConfig> {
    const { data } = await api.get('/microsoft/config')
    return data
  },

  /**
   * Get Microsoft connection status
   */
  async getStatus(): Promise<MicrosoftAccount> {
    const { data } = await api.get('/microsoft/status')
    return data
  },

  /**
   * Get authorization URL for Microsoft OAuth
   */
  async getAuthUrl(): Promise<{ auth_url: string }> {
    const { data } = await api.get('/microsoft/connect')
    return data
  },

  /**
   * Disconnect Microsoft account
   */
  async disconnect(): Promise<void> {
    await api.post('/microsoft/disconnect')
  },
}

// ============================================================================
// Email (Outlook)
// ============================================================================

export const outlookApi = {
  /**
   * Get email messages
   */
  async getMessages(params?: {
    folder?: string
    top?: number
    skip?: number
    search?: string
    filter?: string
  }): Promise<{ value: OutlookEmail[]; '@odata.nextLink'?: string }> {
    const { data } = await api.get('/microsoft/email/messages', { params })
    return data
  },

  /**
   * Get a single message
   */
  async getMessage(messageId: string): Promise<OutlookEmail> {
    const { data } = await api.get(`/microsoft/email/messages/${messageId}`)
    return data
  },

  /**
   * Get message attachments
   */
  async getAttachments(messageId: string): Promise<{ attachments: unknown[] }> {
    const { data } = await api.get(`/microsoft/email/messages/${messageId}/attachments`)
    return data
  },

  /**
   * Download attachment
   */
  async downloadAttachment(messageId: string, attachmentId: string): Promise<Blob> {
    const { data } = await api.get(
      `/microsoft/email/messages/${messageId}/attachments/${attachmentId}/download`,
      { responseType: 'blob' }
    )
    return data
  },

  /**
   * Send an email
   */
  async sendMessage(params: {
    to: string[]
    subject: string
    body: string
    cc?: string[]
    bcc?: string[]
    is_html?: boolean
    attachments?: Array<{ name: string; contentType: string; contentBytes: string }>
  }): Promise<void> {
    await api.post('/microsoft/email/send', params)
  },

  /**
   * Reply to a message
   */
  async reply(messageId: string, body: string, replyAll = false): Promise<void> {
    await api.post(`/microsoft/email/messages/${messageId}/reply`, {
      body,
      reply_all: replyAll,
    })
  },

  /**
   * Forward a message
   */
  async forward(messageId: string, to: string[], comment?: string): Promise<void> {
    await api.post(`/microsoft/email/messages/${messageId}/forward`, { to, comment })
  },

  /**
   * Mark message as read/unread
   */
  async markRead(messageId: string, isRead = true): Promise<OutlookEmail> {
    const { data } = await api.patch(`/microsoft/email/messages/${messageId}/read`, {
      is_read: isRead,
    })
    return data
  },

  /**
   * Move message to folder
   */
  async moveMessage(messageId: string, destination: string): Promise<OutlookEmail> {
    const { data } = await api.post(`/microsoft/email/messages/${messageId}/move`, {
      destination,
    })
    return data
  },

  /**
   * Delete a message
   */
  async deleteMessage(messageId: string): Promise<void> {
    await api.delete(`/microsoft/email/messages/${messageId}`)
  },

  /**
   * Get mail folders
   */
  async getFolders(): Promise<{ folders: OutlookFolder[] }> {
    const { data } = await api.get('/microsoft/email/folders')
    return data
  },

  /**
   * Create a draft
   */
  async createDraft(params: {
    to: string[]
    subject: string
    body: string
    is_html?: boolean
  }): Promise<OutlookEmail> {
    const { data } = await api.post('/microsoft/email/drafts', params)
    return data
  },

  /**
   * Sync emails for a contact
   */
  async syncContactEmails(
    contactEmail: string,
    since?: string
  ): Promise<{ messages: OutlookEmail[] }> {
    const { data } = await api.get(`/microsoft/email/sync/contact/${contactEmail}`, {
      params: { since },
    })
    return data
  },
}

// ============================================================================
// Calendar
// ============================================================================

export const calendarApi = {
  /**
   * Get calendars
   */
  async getCalendars(): Promise<{ calendars: unknown[] }> {
    const { data } = await api.get('/microsoft/calendar/calendars')
    return data
  },

  /**
   * Get calendar events
   */
  async getEvents(params?: {
    calendar_id?: string
    start?: string
    end?: string
    top?: number
  }): Promise<{ events: CalendarEvent[] }> {
    const { data } = await api.get('/microsoft/calendar/events', { params })
    return data
  },

  /**
   * Get a single event
   */
  async getEvent(eventId: string): Promise<CalendarEvent> {
    const { data } = await api.get(`/microsoft/calendar/events/${eventId}`)
    return data
  },

  /**
   * Create an event
   */
  async createEvent(params: {
    subject: string
    start: string
    end: string
    attendees?: string[]
    location?: string
    body?: string
    is_online_meeting?: boolean
    calendar_id?: string
    is_all_day?: boolean
  }): Promise<CalendarEvent> {
    const { data } = await api.post('/microsoft/calendar/events', params)
    return data
  },

  /**
   * Create a Teams meeting
   */
  async createTeamsMeeting(params: {
    subject: string
    start: string
    end: string
    attendees: string[]
    body?: string
  }): Promise<CalendarEvent> {
    const { data } = await api.post('/microsoft/calendar/events/teams', params)
    return data
  },

  /**
   * Update an event
   */
  async updateEvent(
    eventId: string,
    params: {
      subject?: string
      start?: string
      end?: string
      location?: string
      body?: string
      attendees?: string[]
    }
  ): Promise<CalendarEvent> {
    const { data } = await api.patch(`/microsoft/calendar/events/${eventId}`, params)
    return data
  },

  /**
   * Delete an event
   */
  async deleteEvent(eventId: string): Promise<void> {
    await api.delete(`/microsoft/calendar/events/${eventId}`)
  },

  /**
   * Respond to an event invitation
   */
  async respondToEvent(
    eventId: string,
    response: 'accept' | 'tentativelyAccept' | 'decline',
    comment?: string,
    sendResponse = true
  ): Promise<void> {
    await api.post(`/microsoft/calendar/events/${eventId}/respond`, {
      response,
      comment,
      send_response: sendResponse,
    })
  },

  /**
   * Get free/busy schedule
   */
  async getSchedule(params: {
    emails: string[]
    start: string
    end: string
    interval?: number
  }): Promise<{ schedules: unknown[] }> {
    const { data } = await api.post('/microsoft/calendar/schedule', params)
    return data
  },

  /**
   * Find available meeting times
   */
  async findMeetingTimes(params: {
    attendees: string[]
    duration?: number
    start?: string
    end?: string
  }): Promise<{ suggestions: unknown[] }> {
    const { data } = await api.post('/microsoft/calendar/find-times', params)
    return data
  },
}

// ============================================================================
// Files (OneDrive/SharePoint)
// ============================================================================

// ============================================================================
// Teams
// ============================================================================

export interface TeamsTeam {
  id: string
  displayName: string
  description?: string
  webUrl?: string
}

export interface TeamsChannel {
  id: string
  displayName: string
  description?: string
  webUrl?: string
  membershipType?: string
}

export interface TeamsMessage {
  id: string
  createdDateTime: string
  from?: {
    user?: { displayName: string; id: string }
  }
  body: {
    contentType: string
    content: string
  }
  webUrl?: string
}

export interface TeamsChat {
  id: string
  topic?: string
  chatType: 'oneOnOne' | 'group' | 'meeting'
  createdDateTime: string
  webUrl?: string
}

export interface TeamsConfig {
  configured: boolean
  default_team_id?: string
  default_team_name?: string
  default_channel_id?: string
  default_channel_name?: string
  notifications_enabled: boolean
  notify_on_deal_won?: boolean
  notify_on_deal_lost?: boolean
  notify_on_task_overdue?: boolean
}

export const teamsApi = {
  /**
   * Get teams the user has joined
   */
  async getJoinedTeams(): Promise<TeamsTeam[]> {
    const { data } = await api.get('/microsoft/teams/joined')
    return data.data
  },

  /**
   * Get channels in a team
   */
  async getTeamChannels(teamId: string): Promise<TeamsChannel[]> {
    const { data } = await api.get(`/microsoft/teams/${teamId}/channels`)
    return data.data
  },

  /**
   * Get messages from a channel
   */
  async getChannelMessages(
    teamId: string,
    channelId: string,
    top = 50
  ): Promise<TeamsMessage[]> {
    const { data } = await api.get(
      `/microsoft/teams/${teamId}/channels/${channelId}/messages`,
      { params: { top } }
    )
    return data.data
  },

  /**
   * Send a message to a channel
   */
  async sendChannelMessage(
    teamId: string,
    channelId: string,
    content: string,
    contentType: 'text' | 'html' = 'html'
  ): Promise<TeamsMessage> {
    const { data } = await api.post(
      `/microsoft/teams/${teamId}/channels/${channelId}/messages`,
      { content, content_type: contentType }
    )
    return data.data
  },

  /**
   * Get user's chats
   */
  async getUserChats(top = 50): Promise<TeamsChat[]> {
    const { data } = await api.get('/microsoft/teams/chats', { params: { top } })
    return data.data
  },

  /**
   * Create a new chat
   */
  async createChat(members: string[], topic?: string): Promise<TeamsChat> {
    const { data } = await api.post('/microsoft/teams/chats', { members, topic })
    return data.data
  },

  /**
   * Send a message to a chat
   */
  async sendChatMessage(
    chatId: string,
    content: string,
    contentType: 'text' | 'html' = 'html'
  ): Promise<TeamsMessage> {
    const { data } = await api.post(`/microsoft/teams/chats/${chatId}/messages`, {
      content,
      content_type: contentType,
    })
    return data.data
  },

  /**
   * Send a formatted CRM notification to a Teams channel
   */
  async sendCrmNotification(params: {
    team_id: string
    channel_id: string
    notification_type?: string
    title: string
    message: string
    link_url?: string
    link_text?: string
  }): Promise<TeamsMessage> {
    const { data } = await api.post('/microsoft/teams/notifications/crm', params)
    return data.data
  },

  /**
   * Send deal update notification
   */
  async notifyDealUpdate(params: {
    team_id: string
    channel_id: string
    deal_name: string
    old_stage: string
    new_stage: string
    deal_value: number
    deal_url: string
  }): Promise<TeamsMessage> {
    const { data } = await api.post('/microsoft/teams/notifications/deal-update', params)
    return data.data
  },

  /**
   * Send task due notification
   */
  async notifyTaskDue(params: {
    team_id: string
    channel_id: string
    task_title: string
    project_name: string
    due_date: string
    assignee: string
    task_url: string
  }): Promise<TeamsMessage> {
    const { data } = await api.post('/microsoft/teams/notifications/task-due', params)
    return data.data
  },

  /**
   * Get Teams notification configuration
   */
  async getConfig(): Promise<TeamsConfig> {
    const { data } = await api.get('/microsoft/teams/config')
    return data.data
  },

  /**
   * Update Teams notification configuration
   */
  async updateConfig(config: Partial<TeamsConfig>): Promise<TeamsConfig> {
    const { data } = await api.put('/microsoft/teams/config', config)
    return data.data
  },
}

// ============================================================================
// Files (OneDrive/SharePoint)
// ============================================================================

export const filesApi = {
  /**
   * Get user's default drive
   */
  async getDrive(): Promise<unknown> {
    const { data } = await api.get('/microsoft/files/drive')
    return data
  },

  /**
   * Get all accessible drives
   */
  async getDrives(): Promise<{ drives: unknown[] }> {
    const { data } = await api.get('/microsoft/files/drives')
    return data
  },

  /**
   * List items in a folder
   */
  async listItems(params?: {
    path?: string
    drive_id?: string
  }): Promise<{ items: (OneDriveFile | OneDriveFolder)[] }> {
    const { data } = await api.get('/microsoft/files/items', { params })
    return data
  },

  /**
   * Get an item by ID
   */
  async getItem(itemId: string, driveId?: string): Promise<OneDriveFile | OneDriveFolder> {
    const { data } = await api.get(`/microsoft/files/items/${itemId}`, {
      params: { drive_id: driveId },
    })
    return data
  },

  /**
   * Search for files
   */
  async search(params: {
    q: string
    drive_id?: string
    top?: number
  }): Promise<{ items: OneDriveFile[] }> {
    const { data } = await api.get('/microsoft/files/search', { params })
    return data
  },

  /**
   * Upload a file
   */
  async uploadFile(
    file: File,
    folderPath: string,
    driveId?: string
  ): Promise<OneDriveFile> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder_path', folderPath)
    if (driveId) {
      formData.append('drive_id', driveId)
    }

    const { data } = await api.post('/microsoft/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  },

  /**
   * Create a folder
   */
  async createFolder(
    folderName: string,
    parentPath: string,
    driveId?: string
  ): Promise<OneDriveFolder> {
    const { data } = await api.post('/microsoft/files/folders', {
      folder_name: folderName,
      parent_path: parentPath,
      drive_id: driveId,
    })
    return data
  },

  /**
   * Download a file
   */
  async downloadFile(itemId: string, driveId?: string): Promise<Blob> {
    const { data } = await api.get(`/microsoft/files/items/${itemId}/download`, {
      params: { drive_id: driveId },
      responseType: 'blob',
    })
    return data
  },

  /**
   * Delete an item
   */
  async deleteItem(itemId: string, driveId?: string): Promise<void> {
    await api.delete(`/microsoft/files/items/${itemId}`, {
      params: { drive_id: driveId },
    })
  },

  /**
   * Move an item
   */
  async moveItem(
    itemId: string,
    newParentPath: string,
    newName?: string,
    driveId?: string
  ): Promise<OneDriveFile | OneDriveFolder> {
    const { data } = await api.post(`/microsoft/files/items/${itemId}/move`, {
      new_parent_path: newParentPath,
      new_name: newName,
      drive_id: driveId,
    })
    return data
  },

  /**
   * Copy an item
   */
  async copyItem(
    itemId: string,
    newParentPath: string,
    newName?: string,
    driveId?: string
  ): Promise<{ monitor_url: string }> {
    const { data } = await api.post(`/microsoft/files/items/${itemId}/copy`, {
      new_parent_path: newParentPath,
      new_name: newName,
      drive_id: driveId,
    })
    return data
  },

  /**
   * Create a sharing link
   */
  async shareItem(
    itemId: string,
    type: 'view' | 'edit' = 'view',
    scope: 'anonymous' | 'organization' | 'users' = 'anonymous',
    driveId?: string
  ): Promise<{ link: { webUrl: string } }> {
    const { data } = await api.post(`/microsoft/files/items/${itemId}/share`, {
      type,
      scope,
      drive_id: driveId,
    })
    return data
  },

  /**
   * Ensure entity folder structure exists
   */
  async ensureEntityFolder(
    entityType: string,
    entityId: string,
    entityName: string
  ): Promise<{ folder_path: string }> {
    const { data } = await api.post('/microsoft/files/entity-folder', {
      entity_type: entityType,
      entity_id: entityId,
      entity_name: entityName,
    })
    return data
  },

  /**
   * Get storage quota information from the default drive
   */
  async getStorageQuota(): Promise<{ used: number; remaining: number; total: number }> {
    const { data } = await api.get<{
      quota?: {
        used?: number
        remaining?: number
        total?: number
      }
    }>('/microsoft/files/drive')
    return {
      used: data.quota?.used || 0,
      remaining: data.quota?.remaining || 0,
      total: data.quota?.total || 0,
    }
  },

  // SharePoint methods
  /**
   * Get SharePoint sites the user follows
   */
  async getSharePointSites(): Promise<{ sites: SharePointSite[] }> {
    const { data } = await api.get('/microsoft/files/sharepoint/sites')
    return data
  },

  /**
   * Search for SharePoint sites
   */
  async searchSharePointSites(query: string, top = 25): Promise<{ sites: SharePointSite[] }> {
    const { data } = await api.get('/microsoft/files/sharepoint/sites/search', {
      params: { q: query, top },
    })
    return data
  },

  /**
   * Get root SharePoint site
   */
  async getRootSharePointSite(): Promise<SharePointSite> {
    const { data } = await api.get('/microsoft/files/sharepoint/sites/root')
    return data
  },

  /**
   * Get a SharePoint site by ID
   */
  async getSharePointSite(siteId: string): Promise<SharePointSite> {
    const { data } = await api.get(`/microsoft/files/sharepoint/sites/${siteId}`)
    return data
  },

  /**
   * Get document libraries in a SharePoint site
   */
  async getSharePointDrives(siteId: string): Promise<{ drives: unknown[] }> {
    const { data } = await api.get(`/microsoft/files/sharepoint/sites/${siteId}/drives`)
    return data
  },

  /**
   * Get lists in a SharePoint site
   */
  async getSharePointLists(siteId: string): Promise<{ lists: unknown[] }> {
    const { data } = await api.get(`/microsoft/files/sharepoint/sites/${siteId}/lists`)
    return data
  },

  /**
   * Get subsites of a SharePoint site
   */
  async getSharePointSubsites(siteId: string): Promise<{ subsites: SharePointSite[] }> {
    const { data } = await api.get(`/microsoft/files/sharepoint/sites/${siteId}/subsites`)
    return data
  },
}

// ============================================================================
// SharePoint Types
// ============================================================================

export interface SharePointSite {
  id: string
  name: string
  displayName: string
  webUrl: string
  description?: string
  createdDateTime?: string
  lastModifiedDateTime?: string
  siteCollection?: {
    hostname: string
  }
}

// ============================================================================
// Planner Types
// ============================================================================

export interface PlannerPlan {
  id: string
  title: string
  owner: string
  createdDateTime: string
  createdBy?: { user?: { displayName?: string; id?: string } }
  container?: { containerId: string; type: string }
}

export interface PlannerBucket {
  id: string
  name: string
  planId: string
  orderHint: string
}

export interface PlannerTask {
  id: string
  planId: string
  bucketId: string
  title: string
  orderHint: string
  percentComplete: number
  priority?: number
  startDateTime?: string
  dueDateTime?: string
  hasDescription?: boolean
  previewType?: string
  createdDateTime: string
  createdBy?: { user?: { displayName?: string; id?: string } }
  assignments?: Record<string, { assignedBy?: unknown; assignedDateTime?: string; orderHint?: string }>
  '@odata.etag'?: string
  details?: PlannerTaskDetails
}

export interface PlannerTaskDetails {
  id: string
  description?: string
  previewType?: string
  checklist?: Record<string, { title: string; isChecked: boolean; orderHint: string }>
  references?: Record<string, { alias?: string; type?: string; previewPriority?: string }>
  '@odata.etag'?: string
}

export interface PlannerGroup {
  id: string
  displayName: string
  description?: string
  mail?: string
  groupTypes?: string[]
}

// ============================================================================
// Planner API
// ============================================================================

export const plannerApi = {
  // Plans
  /**
   * Get all plans the user has access to
   */
  async getMyPlans(): Promise<{ plans: PlannerPlan[] }> {
    const { data } = await api.get('/microsoft/planner/plans')
    return data
  },

  /**
   * Get a specific plan
   */
  async getPlan(planId: string, includeDetails = false): Promise<PlannerPlan & { buckets?: PlannerBucket[]; tasks?: PlannerTask[] }> {
    const { data } = await api.get(`/microsoft/planner/plans/${planId}`, {
      params: { include_details: includeDetails },
    })
    return data
  },

  /**
   * Get plan details (category descriptions)
   */
  async getPlanDetails(planId: string): Promise<unknown> {
    const { data } = await api.get(`/microsoft/planner/plans/${planId}/details`)
    return data
  },

  // Buckets
  /**
   * Get all buckets in a plan
   */
  async getPlanBuckets(planId: string): Promise<{ buckets: PlannerBucket[] }> {
    const { data } = await api.get(`/microsoft/planner/plans/${planId}/buckets`)
    return data
  },

  /**
   * Create a new bucket
   */
  async createBucket(planId: string, name: string): Promise<PlannerBucket> {
    const { data } = await api.post('/microsoft/planner/buckets', { plan_id: planId, name })
    return data
  },

  /**
   * Get a specific bucket
   */
  async getBucket(bucketId: string): Promise<PlannerBucket> {
    const { data } = await api.get(`/microsoft/planner/buckets/${bucketId}`)
    return data
  },

  // Tasks
  /**
   * Get all tasks in a plan
   */
  async getPlanTasks(planId: string): Promise<{ tasks: PlannerTask[] }> {
    const { data } = await api.get(`/microsoft/planner/plans/${planId}/tasks`)
    return data
  },

  /**
   * Get all tasks in a bucket
   */
  async getBucketTasks(bucketId: string): Promise<{ tasks: PlannerTask[] }> {
    const { data } = await api.get(`/microsoft/planner/buckets/${bucketId}/tasks`)
    return data
  },

  /**
   * Get all tasks assigned to the current user
   */
  async getMyTasks(): Promise<{ tasks: PlannerTask[] }> {
    const { data } = await api.get('/microsoft/planner/tasks/my')
    return data
  },

  /**
   * Get a specific task
   */
  async getTask(taskId: string, includeDetails = false): Promise<PlannerTask> {
    const { data } = await api.get(`/microsoft/planner/tasks/${taskId}`, {
      params: { include_details: includeDetails },
    })
    return data
  },

  /**
   * Get task details (description, checklist)
   */
  async getTaskDetails(taskId: string): Promise<PlannerTaskDetails> {
    const { data } = await api.get(`/microsoft/planner/tasks/${taskId}/details`)
    return data
  },

  /**
   * Create a new task
   */
  async createTask(params: {
    plan_id: string
    bucket_id: string
    title: string
    assignments?: Record<string, unknown>
    due_date?: string
    priority?: number
    start_date?: string
  }): Promise<PlannerTask> {
    const { data } = await api.post('/microsoft/planner/tasks', params)
    return data
  },

  /**
   * Update a task
   */
  async updateTask(taskId: string, etag: string, updates: Partial<PlannerTask>): Promise<PlannerTask> {
    const { data } = await api.patch(`/microsoft/planner/tasks/${taskId}`, { etag, ...updates })
    return data
  },

  /**
   * Update task details
   */
  async updateTaskDetails(taskId: string, etag: string, updates: Partial<PlannerTaskDetails>): Promise<PlannerTaskDetails> {
    const { data } = await api.patch(`/microsoft/planner/tasks/${taskId}/details`, { etag, ...updates })
    return data
  },

  /**
   * Delete a task
   */
  async deleteTask(taskId: string, etag: string): Promise<void> {
    await api.delete(`/microsoft/planner/tasks/${taskId}`, { params: { etag } })
  },

  /**
   * Complete a task
   */
  async completeTask(taskId: string, etag: string): Promise<PlannerTask> {
    const { data } = await api.post(`/microsoft/planner/tasks/${taskId}/complete`, { etag })
    return data
  },

  /**
   * Assign a task to a user
   */
  async assignTask(taskId: string, etag: string, userId: string): Promise<PlannerTask> {
    const { data } = await api.post(`/microsoft/planner/tasks/${taskId}/assign`, { etag, user_id: userId })
    return data
  },

  /**
   * Move a task to a different bucket
   */
  async moveTask(taskId: string, etag: string, bucketId: string): Promise<PlannerTask> {
    const { data } = await api.post(`/microsoft/planner/tasks/${taskId}/move`, { etag, bucket_id: bucketId })
    return data
  },

  // Groups
  /**
   * Get Microsoft 365 groups the user is a member of
   */
  async getMyGroups(): Promise<{ groups: PlannerGroup[] }> {
    const { data } = await api.get('/microsoft/planner/groups')
    return data
  },

  /**
   * Get a specific group
   */
  async getGroup(groupId: string): Promise<PlannerGroup> {
    const { data } = await api.get(`/microsoft/planner/groups/${groupId}`)
    return data
  },

  /**
   * Get all plans in a group
   */
  async getGroupPlans(groupId: string): Promise<{ plans: PlannerPlan[] }> {
    const { data } = await api.get(`/microsoft/planner/groups/${groupId}/plans`)
    return data
  },
}

// ============================================================================
// Bookings Types
// ============================================================================

export interface BookingBusiness {
  id: string
  displayName: string
  businessType?: string
  phone?: string
  email?: string
  webSiteUrl?: string
  defaultCurrencyIso?: string
  address?: {
    street?: string
    city?: string
    state?: string
    countryOrRegion?: string
    postalCode?: string
  }
  schedulingPolicy?: {
    timeSlotInterval?: string
    minimumLeadTime?: string
    maximumAdvance?: string
    sendConfirmationsToOwner?: boolean
    allowStaffSelection?: boolean
  }
}

export interface BookingService {
  id: string
  displayName: string
  description?: string
  defaultDuration: string
  defaultPrice: number
  defaultPriceType: string
  isHiddenFromCustomers?: boolean
  isLocationOnline?: boolean
  staffMemberIds?: string[]
  maximumAttendeesCount?: number
  notes?: string
  preBuffer?: string
  postBuffer?: string
  schedulingPolicy?: unknown
}

export interface BookingStaffMember {
  id: string
  displayName: string
  emailAddress?: string
  phone?: string
  role?: string
  availabilityIsAffectedByPersonalCalendar?: boolean
  colorIndex?: number
  timeZone?: string
  useBusinessHours?: boolean
  workingHours?: unknown[]
}

export interface BookingCustomer {
  id: string
  displayName: string
  emailAddress?: string
  phone?: string
}

export interface BookingAppointment {
  id: string
  serviceId: string
  serviceName?: string
  serviceLocation?: {
    displayName?: string
    locationType?: string
    uniqueId?: string
  }
  startDateTime: {
    dateTime: string
    timeZone: string
  }
  endDateTime: {
    dateTime: string
    timeZone: string
  }
  duration?: string
  customers?: {
    displayName?: string
    emailAddress?: string
    phone?: string
    notes?: string
  }[]
  staffMemberIds?: string[]
  isLocationOnline?: boolean
  onlineMeetingUrl?: string
  customerNotes?: string
  customerTimeZone?: string
  filledAttendeesCount?: number
  maximumAttendeesCount?: number
  priceType?: string
  price?: number
}

// ============================================================================
// Bookings API
// ============================================================================

export const bookingsApi = {
  // Businesses
  /**
   * Get all booking businesses the user has access to
   */
  async getBusinesses(): Promise<{ businesses: BookingBusiness[] }> {
    const { data } = await api.get('/microsoft/bookings/businesses')
    return data
  },

  /**
   * Get a specific booking business
   */
  async getBusiness(businessId: string): Promise<BookingBusiness> {
    const { data } = await api.get(`/microsoft/bookings/businesses/${businessId}`)
    return data
  },

  // Services
  /**
   * Get all services for a booking business
   */
  async getServices(businessId: string): Promise<{ services: BookingService[] }> {
    const { data } = await api.get(`/microsoft/bookings/businesses/${businessId}/services`)
    return data
  },

  /**
   * Get a specific service
   */
  async getService(businessId: string, serviceId: string): Promise<BookingService> {
    const { data } = await api.get(`/microsoft/bookings/businesses/${businessId}/services/${serviceId}`)
    return data
  },

  /**
   * Create a new service
   */
  async createService(businessId: string, params: {
    display_name: string
    duration_minutes?: number
    price?: number
    price_type?: string
    description?: string
    is_hidden?: boolean
    is_online?: boolean
    staff_member_ids?: string[]
  }): Promise<BookingService> {
    const { data } = await api.post(`/microsoft/bookings/businesses/${businessId}/services`, params)
    return data
  },

  /**
   * Update a service
   */
  async updateService(businessId: string, serviceId: string, updates: Partial<BookingService>): Promise<BookingService> {
    const { data } = await api.patch(`/microsoft/bookings/businesses/${businessId}/services/${serviceId}`, updates)
    return data
  },

  /**
   * Delete a service
   */
  async deleteService(businessId: string, serviceId: string): Promise<void> {
    await api.delete(`/microsoft/bookings/businesses/${businessId}/services/${serviceId}`)
  },

  // Staff
  /**
   * Get all staff members for a booking business
   */
  async getStaffMembers(businessId: string): Promise<{ staff: BookingStaffMember[] }> {
    const { data } = await api.get(`/microsoft/bookings/businesses/${businessId}/staff`)
    return data
  },

  /**
   * Get a specific staff member
   */
  async getStaffMember(businessId: string, staffId: string): Promise<BookingStaffMember> {
    const { data } = await api.get(`/microsoft/bookings/businesses/${businessId}/staff/${staffId}`)
    return data
  },

  // Customers
  /**
   * Get all customers for a booking business
   */
  async getCustomers(businessId: string): Promise<{ customers: BookingCustomer[] }> {
    const { data } = await api.get(`/microsoft/bookings/businesses/${businessId}/customers`)
    return data
  },

  /**
   * Create a new customer
   */
  async createCustomer(businessId: string, params: {
    display_name: string
    email?: string
    phone?: string
  }): Promise<BookingCustomer> {
    const { data } = await api.post(`/microsoft/bookings/businesses/${businessId}/customers`, params)
    return data
  },

  // Appointments
  /**
   * Get appointments for a booking business
   */
  async getAppointments(businessId: string, params?: {
    start?: string
    end?: string
  }): Promise<{ appointments: BookingAppointment[] }> {
    const { data } = await api.get(`/microsoft/bookings/businesses/${businessId}/appointments`, { params })
    return data
  },

  /**
   * Get a specific appointment
   */
  async getAppointment(businessId: string, appointmentId: string): Promise<BookingAppointment> {
    const { data } = await api.get(`/microsoft/bookings/businesses/${businessId}/appointments/${appointmentId}`)
    return data
  },

  /**
   * Create a new appointment
   */
  async createAppointment(businessId: string, params: {
    service_id: string
    start_time: string
    end_time: string
    customer_name?: string
    customer_email?: string
    customer_phone?: string
    staff_member_ids?: string[]
    is_online?: boolean
    notes?: string
  }): Promise<BookingAppointment> {
    const { data } = await api.post(`/microsoft/bookings/businesses/${businessId}/appointments`, params)
    return data
  },

  /**
   * Update an appointment
   */
  async updateAppointment(businessId: string, appointmentId: string, updates: Partial<BookingAppointment>): Promise<BookingAppointment> {
    const { data } = await api.patch(`/microsoft/bookings/businesses/${businessId}/appointments/${appointmentId}`, updates)
    return data
  },

  /**
   * Cancel an appointment
   */
  async cancelAppointment(businessId: string, appointmentId: string, message?: string): Promise<void> {
    await api.post(`/microsoft/bookings/businesses/${businessId}/appointments/${appointmentId}/cancel`, { message })
  },

  // Calendar View
  /**
   * Get calendar view of appointments
   */
  async getCalendarView(businessId: string, start: string, end: string): Promise<{ appointments: BookingAppointment[] }> {
    const { data } = await api.get(`/microsoft/bookings/businesses/${businessId}/calendar`, {
      params: { start, end },
    })
    return data
  },

  // Availability
  /**
   * Get staff availability for scheduling
   */
  async getStaffAvailability(businessId: string, params: {
    staff_ids: string[]
    start: string
    end: string
  }): Promise<{ availability: unknown[] }> {
    const { data } = await api.post(`/microsoft/bookings/businesses/${businessId}/availability`, params)
    return data
  },
}

export default {
  ...microsoftApi,
  outlook: outlookApi,
  calendar: calendarApi,
  files: filesApi,
  teams: teamsApi,
  planner: plannerApi,
  bookings: bookingsApi,
}
